import { isAppDebugEnabled, logExpandedDebug } from '@/utils/debug/expandedConsoleLog';
import { extractWgs84Point } from '@/utils/defect/extractWgs84Point';
import { summarizeHierarchyTree } from '@/features/defect-drawer/utils/moduleListSyncDebug';

export const DEFECT_MODULE_LINK_PREFIX = '[DEFECT-MODULE-LINK]';

/**
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
export function logDefectModuleLink(event, detail = {}) {
  logExpandedDebug(DEFECT_MODULE_LINK_PREFIX, event, detail);
}

/**
 * @param {unknown} defect
 */
export function summarizeDefectModuleLinkState(defect) {
  if (!defect || typeof defect !== 'object') {
    return { present: false };
  }

  const solarModule = defect.solarModule ?? defect._embedded?.solarModule ?? null;
  const point = extractWgs84Point(defect.defectLocation);
  const shape = defect.defectShape ?? null;

  return {
    present: true,
    defectId: defect.id ?? defect.seq ?? null,
    detectWay: defect.detectWay ?? null,
    aiAnalysisJobSeq: defect.aiAnalysisJobSeq ?? null,
    defectLocation: point,
    defectShape: shape
      ? {
          leftX: shape.left_x ?? shape.leftX ?? null,
          topY: shape.top_y ?? shape.topY ?? null,
          width: shape.width ?? null,
          height: shape.height ?? null
        }
      : null,
    solarModuleSeq: solarModule?.id ?? solarModule?.seq ?? defect.solarModuleSeq ?? null,
    moduleId: solarModule?.moduleId ?? defect.moduleId ?? null,
    solarThermalImageId:
      defect.solarThermalImage?.id ??
      defect.solarThermalImage ??
      defect.solar_thermal_image_seq ??
      null
  };
}

/**
 * @param {unknown[]} modules
 * @param {number} [limit]
 */
export function summarizeNearModules(modules, limit = 5) {
  const list = Array.isArray(modules) ? modules : [];
  return {
    count: list.length,
    sample: list.slice(0, limit).map(m => ({
      seq: m?.id ?? m?.seq ?? null,
      moduleId: m?.moduleId ?? null,
      distanceMeters: m?.distanceMeters ?? m?.distance ?? null
    }))
  };
}

/**
 * PATCH 전후·near-modules·store 행을 한 번에 비교한다.
 * @param {import('vuex').Store} [store]
 * @param {number | string} defectId
 * @param {{
 *   trigger?: string,
 *   before?: unknown,
 *   after?: unknown,
 *   patchPoint?: { lon: number, lat: number } | null,
 *   nearModules?: unknown[],
 *   thermalImageId?: number | string | null
 * }} ctx
 */
export function logDefectModuleLinkComparison(store, defectId, ctx = {}) {
  if (!isAppDebugEnabled()) {
    return;
  }
  const wfsRows = store?.state?.dashboard?.wfsDefectRows || [];
  const wfsRow = wfsRows.find(d => Number(d?.seq) === Number(defectId)) || null;
  const hierarchy = store?.state?.dashboard?.siteDesignModuleHierarchy || [];

  const before = summarizeDefectModuleLinkState(ctx.before);
  const after = summarizeDefectModuleLinkState(ctx.after);

  logDefectModuleLink('comparison', {
    trigger: ctx.trigger ?? 'unknown',
    defectId,
    thermalImageId: ctx.thermalImageId ?? before.solarThermalImageId ?? null,
    aiReRunRequiredForThisStep: false,
    note:
      '저장 후 PATCH는 AI 재요청과 무관하게 findContainingPoint(폴리곤 포함)만 재시도한다. near-modules는 2m 반경(포함+근접).',
    serverBeforePatch: before,
    patchSent: ctx.patchPoint ?? before.defectLocation ?? null,
    serverAfterPatch: after,
    nearModulesWithin2m: summarizeNearModules(ctx.nearModules),
    linkageChanged:
      before.solarModuleSeq !== after.solarModuleSeq ||
      (before.solarModuleSeq == null && after.solarModuleSeq != null),
    storeWfsRow: wfsRow
      ? {
          defectSeq: wfsRow.seq,
          solarModuleSeq: wfsRow.solarModuleSeq ?? null,
          moduleId: wfsRow.moduleId ?? wfsRow.module_id ?? null,
          lng: wfsRow.lng ?? null,
          lat: wfsRow.lat ?? null
        }
      : null,
    siteDesignModules: summarizeHierarchyTree(hierarchy)
  });
}
